package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.api.dto.admin.AdminConversationMessage
import com.merkost.handpass.api.dto.admin.AdminConversationSummary
import com.merkost.handpass.api.dto.admin.AdminMessageSenderRole
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.HandpassRadius
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnAccent
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun AdminConversationsView(
    conversations: AsyncSection<List<AdminConversationSummary>>,
    openConversation: AdminConversationSummary?,
    messages: AsyncSection<List<AdminConversationMessage>>,
    onOpen: (AdminConversationSummary) -> Unit,
    onClose: () -> Unit,
    onRetryConversations: () -> Unit = {},
    onRetryMessages: () -> Unit = {},
) {
    if (openConversation == null) {
        ConversationList(conversations, onOpen, onRetryConversations)
    } else {
        ConversationThread(openConversation, messages, onClose, onRetryMessages)
    }
}

@Composable
private fun ConversationList(
    conversations: AsyncSection<List<AdminConversationSummary>>,
    onOpen: (AdminConversationSummary) -> Unit,
    onRetry: () -> Unit,
) {
    when (conversations) {
        AsyncSection.Loading -> AdminCard { AdminSkeletonTable(rows = 4) }
        is AsyncSection.Failed -> AdminEmptyState(
            title = "Couldn't load conversations",
            description = conversations.message,
            actionLabel = "Retry",
            onAction = onRetry,
        )
        is AsyncSection.Loaded -> {
            val list = conversations.value
            if (list.isEmpty()) {
                AdminEmptyState(title = "No conversations", description = "No message threads to review.")
            } else {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.SM) } }) {
                    list.forEach { ConversationCard(it, onOpen) }
                }
            }
        }
    }
}

@Composable
private fun ConversationCard(conversation: AdminConversationSummary, onOpen: (AdminConversationSummary) -> Unit) {
    AdminCard(paddingScale = AdminCardPadding.Compact, onClick = { onOpen(conversation) }) {
        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.MD) } }) {
            Div({ style { property("flex", "1"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.XXS) } }) {
                Span(
                    attrs = Modifier
                        .color(OnSurface.value())
                        .fontSize(HandpassTypography.Body)
                        .toAttrs()
                ) {
                    Text("${conversation.buyerName ?: "Buyer"} ↔ ${conversation.sellerName ?: "Seller"}")
                }
                conversation.listingTitle?.let {
                    Span(
                        attrs = Modifier
                            .color(OnSurfaceMuted.value())
                            .fontSize(HandpassTypography.Caption)
                            .toAttrs()
                    ) { Text(it) }
                }
                conversation.lastMessagePreview?.let {
                    Span(
                        attrs = Modifier
                            .color(OnSurfaceFaint.value())
                            .fontSize(HandpassTypography.Caption)
                            .toAttrs()
                    ) { Text(it) }
                }
            }
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.FlexEnd); gap(HandpassSpacing.XXS) } }) {
                AdminBadge(label = "${conversation.messageCount} msgs", variant = AdminBadgeVariant.Neutral)
                conversation.lastMessageAt?.let {
                    Span(
                        attrs = Modifier
                            .color(OnSurfaceFaint.value())
                            .fontSize(HandpassTypography.Caption)
                            .toAttrs()
                    ) { Text(it.take(10)) }
                }
            }
        }
    }
}

@Composable
private fun ConversationThread(
    conversation: AdminConversationSummary,
    messages: AsyncSection<List<AdminConversationMessage>>,
    onClose: () -> Unit,
    onRetry: () -> Unit,
) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.MD) } }) {
        AdminButton(label = "← All conversations", onClick = onClose, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
        Span(
            attrs = Modifier
                .color(OnSurfaceMuted.value())
                .fontSize(HandpassTypography.Caption)
                .toAttrs()
        ) {
            Text("${conversation.buyerName ?: "Buyer"} ↔ ${conversation.sellerName ?: "Seller"}" + (conversation.listingTitle?.let { " · $it" } ?: ""))
        }
        when (messages) {
            AsyncSection.Loading -> AdminCard { AdminSkeletonTable(rows = 6) }
            is AsyncSection.Failed -> AdminEmptyState(
                title = "Couldn't load messages",
                description = messages.message,
                actionLabel = "Retry",
                onAction = onRetry,
            )
            is AsyncSection.Loaded -> {
                val list = messages.value
                if (list.isEmpty()) {
                    AdminEmptyState(title = "No messages")
                } else {
                    AdminCard {
                        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(HandpassSpacing.SM) } }) {
                            list.forEach { MessageBubble(it) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MessageBubble(message: AdminConversationMessage) {
    val justify = when (message.senderRole) {
        AdminMessageSenderRole.SELLER -> JustifyContent.FlexEnd
        AdminMessageSenderRole.BUYER -> JustifyContent.FlexStart
        AdminMessageSenderRole.SYSTEM -> JustifyContent.Center
    }
    val bubbleBgMod = when (message.senderRole) {
        AdminMessageSenderRole.SELLER -> Modifier.backgroundColor(Accent.value())
        else -> Modifier.backgroundColor(SurfaceMuted.value())
    }
    val textColorMod = when (message.senderRole) {
        AdminMessageSenderRole.SELLER -> Modifier.color(OnAccent.value())
        else -> Modifier.color(OnSurface.value())
    }
    val metaNameMod = when (message.senderRole) {
        AdminMessageSenderRole.SELLER -> Modifier.color(OnAccent.value())
        else -> Modifier.color(OnSurfaceMuted.value())
    }
    val metaTimeMod = when (message.senderRole) {
        AdminMessageSenderRole.SELLER -> Modifier.color(OnAccent.value()).opacity(0.7)
        else -> Modifier.color(OnSurfaceFaint.value())
    }
    val imageUrl = message.imageUrl
    Div({ style { display(DisplayStyle.Flex); justifyContent(justify); width(100.percent) } }) {
        Div(
            attrs = bubbleBgMod
                .borderRadius(RadiusControl.value())
                .let { if (message.isDeleted) it.opacity(0.5) else it }
                .styleModifier {
                    property("max-width", "72%")
                    property("padding", "${HandpassSpacing.SM}")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "${HandpassSpacing.XXS}")
                }
                .toAttrs()
        ) {
            Div({ style { display(DisplayStyle.Flex); gap(HandpassSpacing.SM); alignItems(AlignItems.Center) } }) {
                Span(
                    attrs = metaNameMod
                        .fontSize(HandpassTypography.XS)
                        .toAttrs()
                ) {
                    Text(message.senderName ?: message.senderRole.name.lowercase().replaceFirstChar { it.uppercase() })
                }
                Span(
                    attrs = metaTimeMod
                        .fontSize(HandpassTypography.XS)
                        .toAttrs()
                ) {
                    Text(message.createdAt.take(16).replace("T", " "))
                }
            }
            if (imageUrl != null) {
                Img(imageUrl) {
                    style {
                        property("max-width", "200px")
                        property("border-radius", "${HandpassRadius.SM}")
                    }
                }
            } else {
                Span(
                    attrs = textColorMod
                        .fontSize(HandpassTypography.Body)
                        .toAttrs()
                ) {
                    Text((if (message.isDeleted) "(deleted) " else "") + (message.body ?: ""))
                }
            }
        }
    }
}
